import { useEffect, useState } from "react";
import { getMyProfile, getPublicPlans, initiateSubscription, subscribeViaWallet, verifySubscription } from "../../api/client";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import LoadingIndicator from "../../components/LoadingIndicator";

// Prices, discount percentages, and the Saver delivery cap all come live
// from admin's Pricing settings (GET /public/plans) — never hardcoded here,
// so an admin change shows up the next time this page loads, no deploy needed.
function buildPlans(plansInfo) {
  if (!plansInfo) return null;
  return {
    plus: {
      name: "FASTSEND Plus",
      icon: "⭐",
      price: plansInfo.plus.price,
      tagline: `Priority dispatch & ${plansInfo.plus.discountPercent}% off every delivery`,
      perks: [
        `${plansInfo.plus.discountPercent}% Off all delivery quotes automatically`,
        "Priority Dispatcher Matching before standard search",
        "Free basic package protection up to ₦10,000",
      ],
    },
    saver: {
      name: "Saver Plan",
      icon: "💚",
      price: plansInfo.saver.price,
      tagline: `Up to ${plansInfo.saver.maxDiscountPercent}% off, for frequent senders`,
      perks: [
        `Up to ${plansInfo.saver.maxDiscountPercent}% off delivery quotes`,
        `Covers up to ${plansInfo.saver.deliveryCap} deliveries per month`,
        "Great for regular / repeat senders",
      ],
    },
  };
}

export default function Subscription() {
  const [profile, setProfile] = useState(null);
  const [plansInfo, setPlansInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [gateway, setGateway] = useState("paystack");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activePlan, setActivePlan] = useState("plus"); // which plan card is being purchased

  const PLANS = buildPlans(plansInfo);

  const load = async () => {
    setLoading(true);
    try {
      const [profileRes, plansRes] = await Promise.all([getMyProfile(), getPublicPlans()]);
      setProfile(profileRes.data);
      setPlansInfo(plansRes.data);
    } catch {
      setError("Could not load plan and profile information");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCardSubscribe = async (plan) => {
    setError("");
    setMessage("");
    setBusy(true);
    try {
      const email = profile?.email || "user@example.com";
      const { data } = await initiateSubscription(gateway, email, plan);
      if (data.isMocked) {
        await verifySubscription(data.reference, gateway, plan);
        setMessage(`🎉 Welcome to ${PLANS[plan].name}! Your discount is now active.`);
        await load();
      } else if (data.checkoutUrl) {
        window.open(data.checkoutUrl, "_blank");
        setMessage("Payment window opened. Complete checkout to activate your subscription.");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Subscription request failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleWalletSubscribe = async (plan) => {
    setError("");
    setMessage("");
    setBusy(true);
    try {
      await subscribeViaWallet(plan);
      setMessage(`🎉 Welcome to ${PLANS[plan].name}! ₦${PLANS[plan].price.toLocaleString()} deducted from your wallet.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Wallet subscription failed.");
    } finally {
      setBusy(false);
    }
  };

  const currentTier = profile?.membershipTier || "standard";
  const isSubscribed = currentTier === "plus" || currentTier === "saver";

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader
          membershipTier={profile?.membershipTier || "standard"}
          backTo="/order"
          backLabel="Send Package"
        />

        <div className="page">
        <div className="brand-header">
          <span className="brand-logo">⭐</span>
          <span className="brand-name">FASTSEND Plans</span>
          <p className="brand-tagline">Save on every delivery with a monthly plan</p>
        </div>

        {loading ? (
          <LoadingIndicator label="Loading membership status..." />
        ) : !PLANS ? (
          <p className="error">{error || "Could not load plan information — please try again."}</p>
        ) : (
          <>
            <div className="delivery-card" style={{ textAlign: "center", marginBottom: "24px" }}>
              <p className="hint" style={{ marginBottom: "4px" }}>Current Plan</p>
              <div style={{ fontSize: "28px", fontWeight: 700, color: isSubscribed ? "var(--text-accent)" : "var(--text-primary)" }}>
                {isSubscribed ? `${PLANS[currentTier].icon} ${PLANS[currentTier].name} Member` : "Standard Tier"}
              </div>
              {isSubscribed && profile.membershipExpiresAt && (
                <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "6px" }}>
                  Renews on {new Date(profile.membershipExpiresAt).toLocaleDateString()}
                  {currentTier === "saver" && ` · ${profile.saverDeliveriesUsed || 0}/${plansInfo.saver.deliveryCap} deliveries used this period`}
                </p>
              )}
            </div>

            {!isSubscribed ? (
              <>
                <div className="role-tabs" style={{ marginBottom: "16px" }}>
                  {Object.keys(PLANS).map(key => (
                    <button
                      key={key}
                      className={`role-tab ${activePlan === key ? "active" : ""}`}
                      onClick={() => setActivePlan(key)}
                    >
                      {PLANS[key].icon} {PLANS[key].name}
                    </button>
                  ))}
                </div>

                <div className="quote-box">
                  <h3>{PLANS[activePlan].name} (₦{PLANS[activePlan].price.toLocaleString()}/month)</h3>
                  <p className="hint" style={{ marginBottom: "8px" }}>{PLANS[activePlan].tagline}</p>
                  <ul style={{ paddingLeft: "20px", marginBottom: "20px", fontSize: "14px", lineHeight: "1.8" }}>
                    {PLANS[activePlan].perks.map((perk, i) => <li key={i}>✅ {perk}</li>)}
                  </ul>

                  <div className="form-field">
                    <label>Payment Gateway</label>
                    <select value={gateway} onChange={e => setGateway(e.target.value)}>
                      <option value="paystack">Paystack</option>
                      <option value="flutterwave">Flutterwave</option>
                    </select>
                  </div>

                  {error && <p className="error">{error}</p>}
                  {message && <p style={{ color: "var(--success)", fontSize: "14px", marginBottom: "8px" }}>{message}</p>}

                  <button disabled={busy} onClick={() => handleCardSubscribe(activePlan)} style={{ marginBottom: "8px" }}>
                    {busy ? "Processing..." : `Subscribe via Card / Transfer (₦${PLANS[activePlan].price.toLocaleString()}/mo)`}
                  </button>

                  <button className="secondary" disabled={busy} onClick={() => handleWalletSubscribe(activePlan)}>
                    Pay ₦{PLANS[activePlan].price.toLocaleString()} from Wallet
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <span className="empty-state-icon">✅</span>
                <p className="empty-state-text">You're enjoying full {PLANS[currentTier].name} benefits on all your orders.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  </div>
);
}
